import 'react-native-get-random-values'

import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Keyboard, KeyboardAvoidingView, ScrollView, Pressable } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { getNextMinimumValidDateTime } from '@ppwr/poker'

import { DS } from '../../design-system'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { CreateScheduledTournamentForm } from './CreateScheduledTournamentForm'
import { MAX_NAME_LENGTH } from './CreateScheduledTournamentForm'
import { styles } from './styles'
import { PressableWrapper } from './pressable-wrapper'
import { WebWrapper } from '@/components/WebWrapper'

/**
 * Parse the blind-timer dropdown value. 0 is a valid selection ("None" — blinds never increase),
 * so only a blank/unparseable value falls back to the default.
 */
const parseBlindTimerMin = (value: string): number => {
  const parsed = Number(value)
  return value !== '' && Number.isFinite(parsed) ? parsed : GAME_CONSTANTS.DEFAULT_BLIND_TIMER_MIN
}

export const CreateScheduledTournamentScreen = observer(() => {
  const router = useRouter()
  const {
    scheduleId: scheduleIdParam,
    hash: hashParam,
    isEdit: isEditParam
  } = useLocalSearchParams<{
    scheduleId?: string
    hash?: string
    isEdit?: string
  }>()
  const scheduleIdParamTyped = scheduleIdParam as Id<'gameSchedules'> | undefined
  const isEdit = Boolean(scheduleIdParamTyped || hashParam || isEditParam === 'true')

  const {
    session: { user },
    game: { playKind }
  } = useStore()
  const scheduleById = useQuery(
    api.gameSchedules.queries.getSchedule,
    scheduleIdParamTyped ? { scheduleId: scheduleIdParamTyped } : 'skip'
  )
  const scheduleByHash = useQuery(api.gameSchedules.queries.getScheduleByHash, hashParam ? { hash: hashParam } : 'skip')
  const scheduleRaw = scheduleIdParamTyped ? scheduleById : scheduleByHash
  const schedule = scheduleRaw != null && 'restricted' in scheduleRaw ? null : scheduleRaw
  const effectiveScheduleId = schedule?._id

  const createPrivateSchedule = useMutation(api.gameSchedules.mutations.createPrivateSchedule)
  const joinPrivateScheduledGame = useMutation(api.gameSchedules.mutations.joinPrivateScheduledGame)
  const updateScheduleMutation = useMutation(api.gameSchedules.mutations.updateSchedule)

  const [scheduleForLater, setScheduleForLater] = useState(true)
  const [tournamentName, setTournamentName] = useState('')
  const [tournamentBlindsLength, setTournamentBlindsLength] = useState(String(GAME_CONSTANTS.DEFAULT_BLIND_TIMER_MIN))
  const [tournamentStartingChips, setTournamentStartingChips] = useState(String(GAME_CONSTANTS.DEFAULT_STARTING_CHIPS))
  const [tournamentActionTime, setTournamentActionTime] = useState(String(GAME_CONSTANTS.DEFAULT_ACTION_TIME_SEC))
  const [scheduledDate, setScheduledDate] = useState(getNextMinimumValidDateTime)
  const [errorMsg, setErrorMsg] = useState('')
  const [nameTooLong, setNameTooLong] = useState(false)
  const [isNameEmpty, setIsNameEmpty] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)
  const scrollViewRef = React.useRef<ScrollView>(null)

  // Reset data-loaded flag when edit params change (e.g. navigate to different edit)
  useEffect(() => {
    if (!scheduleIdParamTyped && !hashParam) setDataLoaded(false)
  }, [scheduleIdParamTyped, hashParam])

  // Populate form when editing and schedule has loaded (by id or by hash)
  useEffect(() => {
    if (!isEdit || !schedule || dataLoaded) return
    setTournamentName(schedule.name)
    // getSchedule/getScheduleByHash return a union across the schedule tables; read the optional
    // config fields defensively since not every variant carries them.
    // `??` (not `||`) so a stored 0 — "no blind increase" — survives the round-trip through edit.
    setTournamentBlindsLength(
      String(('blindTimer' in schedule ? schedule.blindTimer : undefined) ?? GAME_CONSTANTS.DEFAULT_BLIND_TIMER_MIN)
    )
    setTournamentStartingChips(
      String(
        ('startingChips' in schedule ? schedule.startingChips : undefined) || GAME_CONSTANTS.DEFAULT_STARTING_CHIPS
      )
    )
    setTournamentActionTime(
      String(('turnDuration' in schedule ? schedule.turnDuration : undefined) || GAME_CONSTANTS.DEFAULT_ACTION_TIME_SEC)
    )
    setScheduledDate(new Date(schedule.scheduledStartTime))
    setScheduleForLater(true)
    setDataLoaded(true)
  }, [isEdit, schedule, dataLoaded])

  const handleAction = async () => {
    if (!tournamentName) {
      setErrorMsg('Game name required')
      setIsNameEmpty(true)
      return
    }
    if (tournamentName.length > MAX_NAME_LENGTH) {
      setNameTooLong(true)
      return
    }

    const hostId = user?.auth0Id ?? ''
    const hostName = user?.name ?? 'Host'
    const mongoUserId = user?._id?.toString()

    if (!hostId) {
      setErrorMsg('You must be signed in to create a tournament')
      return
    }

    if (scheduleForLater || isEdit) {
      const scheduledStartTime = scheduledDate.getTime()
      if (scheduledStartTime <= Date.now()) {
        setErrorMsg('Scheduled time must be in the future')
        return
      }
    }

    setLoading(true)
    setErrorMsg('')

    try {
      if (isEdit && effectiveScheduleId) {
        await updateScheduleMutation({
          scheduleId: effectiveScheduleId,
          name: tournamentName,
          scheduledStartTime: scheduledDate.getTime(),
          blindTimer: parseBlindTimerMin(tournamentBlindsLength),
          startingChips: Number(tournamentStartingChips) || GAME_CONSTANTS.DEFAULT_STARTING_CHIPS,
          turnDuration: Number(tournamentActionTime) || GAME_CONSTANTS.DEFAULT_TURN_DURATION_SEC
        })
        if (router.canDismiss()) {
          router.dismiss()
        } else {
          router.replace('/(root)/(tabs)/play')
        }
      } else if (scheduleForLater) {
        const result = await createPrivateSchedule({
          name: tournamentName,
          hostId,
          hostName,
          scheduledStartTime: scheduledDate.getTime(),
          blindTimer: parseBlindTimerMin(tournamentBlindsLength),
          startingChips: Number(tournamentStartingChips) || GAME_CONSTANTS.DEFAULT_STARTING_CHIPS,
          turnDuration: Number(tournamentActionTime) || GAME_CONSTANTS.DEFAULT_TURN_DURATION_SEC,
          tableConfigs: [],
          teacherUserId: playKind === 'observer' ? hostId : undefined
        })

        if (result?.hash) {
          router.replace(`/game/create/scheduled-tournament-confirm?hash=${result.hash}`)
        }
      } else {
        // Non-lounge "Start Now": create a private schedule then immediately join it.
        // Schedule 1 min in the future to pass the "must be in future" validation.
        const blindTimerMin = parseBlindTimerMin(tournamentBlindsLength)
        const turnDurationSec = Number(tournamentActionTime) || GAME_CONSTANTS.DEFAULT_TURN_DURATION_SEC
        const startingChips = Number(tournamentStartingChips) || GAME_CONSTANTS.DEFAULT_STARTING_CHIPS
        const scheduledStartTime = Date.now() + 60 * 1000
        const { scheduleId } = await createPrivateSchedule({
          name: tournamentName,
          startingChips,
          blindTimer: blindTimerMin,
          turnDuration: turnDurationSec,
          hostId,
          hostName,
          scheduledStartTime,
          tableConfigs: []
        })
        const joinAsTeacher = playKind === 'observer'
        const { gameId } = await joinPrivateScheduledGame({
          scheduleId,
          userId: hostId,
          userName: hostName,
          mongoUserId,
          joinAsTeacher
        })
        // dismissTo (not replace) tears down the create flow (user-role → schedule) as it navigates
        // into the game, so leaving the game returns to where the flow started rather than UserRoleScreen.
        router.dismissTo({ pathname: '/game/[gameId]', params: { gameId } } as Parameters<typeof router.dismissTo>[0])
      }
    } catch (error) {
      console.error(error)
      setErrorMsg(error instanceof Error ? error.message : 'Failed to create tournament')
    } finally {
      setLoading(false)
    }
  }

  const onBack = () => {
    if (router.canDismiss()) {
      return router.dismiss()
    }
    router.dismissTo('/game/create/new')
  }

  return (
    <KeyboardAvoidingView style={styles.mainContainer}>
      <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
      <DS.Frame style={[styles.safeArea, styles.container]}>
        <WebWrapper style={styles.container}>
          <SafeAreaView style={styles.safeArea}>
            <DS.Frame style={styles.headerContainer}>
              <Pressable onPress={onBack}>
                <DS.Icon name="Arrow---left" size={24} color={'Global/White'} />
              </Pressable>
            </DS.Frame>
            <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
              <PressableWrapper onPress={() => Keyboard.dismiss()}>
                <DS.Frame gap="24">
                  <DS.Text variant="Heading 1/Medium" color="Brand/Blue">
                    {isEdit ? translate('createTournamentScreen.editTitle') : translate('createTournamentScreen.title')}
                  </DS.Text>
                  {isEdit && schedule === undefined && (
                    <DS.Text variant="Body Medium/Regular" color="Global/White">
                      Loading...
                    </DS.Text>
                  )}
                  {isEdit && schedule === null && (
                    <DS.Text variant="Body Medium/Regular" color="Utility/Warning">
                      Schedule not found or no longer active.
                    </DS.Text>
                  )}
                  {(!isEdit || (schedule != null && dataLoaded)) && (
                    <CreateScheduledTournamentForm
                      isNameEmpty={isNameEmpty}
                      setIsNameEmpty={setIsNameEmpty}
                      tournamentName={tournamentName}
                      nameTooLong={nameTooLong}
                      tournamentBlindsLength={tournamentBlindsLength}
                      tournamentStartingChips={tournamentStartingChips}
                      tournamentActionTime={tournamentActionTime}
                      setTournamentName={setTournamentName}
                      setTournamentBlindsLength={setTournamentBlindsLength}
                      setTournamentStartingChips={setTournamentStartingChips}
                      setTournamentActionTime={setTournamentActionTime}
                      scheduledDate={scheduledDate}
                      onScheduledDateChange={setScheduledDate}
                      scheduleForLater={scheduleForLater}
                      onScheduleForLaterChange={setScheduleForLater}
                      isEdit={isEdit}
                    />
                  )}
                </DS.Frame>
              </PressableWrapper>
            </ScrollView>
            <DS.Frame style={styles.buttonContainer}>
              <DS.Button
                type="On Dark"
                variant="Large/Primary"
                disabled={loading || (isEdit && (schedule === undefined || schedule === null || !dataLoaded))}
                onPress={debounce(handleAction, 200)}
              >
                {isEdit
                  ? translate('createTournamentScreen.editButton')
                  : scheduleForLater
                    ? translate('createTournamentScreen.createButton')
                    : translate('common.startNow')}
              </DS.Button>
              <DS.Text variant="Caption/Regular" style={styles.errorMsg} color="Global/White">
                {errorMsg}
              </DS.Text>
            </DS.Frame>
          </SafeAreaView>
        </WebWrapper>
      </DS.Frame>
    </KeyboardAvoidingView>
  )
})
